import { useState, useEffect } from 'react';
import backendApi from '../../../../services/backendApi';

export interface PMTask {
  id: string;
  title: string;
  type: string;
  date: string;
  startTime: string;
  endTime: string;
  assignedTo: string;
  location: string;
  status: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  description?: string;
  isRecurring: boolean;
  recurringPattern?: string;
  color: string;
  equipmentName?: string;
}

interface UsePMTasksOptions {
  siteCode?: string;
  selectedDate?: string;
  autoLoad?: boolean;
}

const CACHE_KEY_PREFIX = 'pm_tasks_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: PMTask[];
  timestamp: number;
}

export const usePMTasks = (options: UsePMTasksOptions = {}) => {
  const { siteCode, selectedDate, autoLoad = true } = options;
  const [tasks, setTasks] = useState<PMTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCacheKey = () => {
    const normalizedSite = (siteCode || 'unknown-site').trim().toUpperCase();
    const normalizedDate = (selectedDate || 'all-dates').trim();
    return `${CACHE_KEY_PREFIX}:${normalizedSite}:${normalizedDate}`;
  };

  const getFromCache = (): PMTask[] | null => {
    try {
      const cached = localStorage.getItem(getCacheKey());
      if (!cached) return null;

      const entry: CacheEntry = JSON.parse(cached);
      const now = Date.now();

      if (now - entry.timestamp > CACHE_DURATION) {
        localStorage.removeItem(getCacheKey());
        return null;
      }

      return entry.data;
    } catch {
      return null;
    }
  };

  const saveToCache = (data: PMTask[]) => {
    try {
      // Avoid persisting empty responses so a temporary no-data state does not hide real tasks.
      if (!Array.isArray(data) || data.length === 0) return;

      const entry: CacheEntry = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(getCacheKey(), JSON.stringify(entry));
    } catch {
      // Cache write failed, continue without cache
    }
  };

  const fetchTasks = async () => {
    if (!siteCode) return;

    setLoading(true);
    setError(null);

    try {
      // Try cache first
      const cached = getFromCache();
      if (cached) {
        setTasks(cached);
        setLoading(false);
        return;
      }

      // First try the selected date window (if provided), then fallback to all dates.
      let res = await (backendApi as any).getPMCalendarEvents?.(
        siteCode,
        selectedDate || undefined,
        selectedDate || undefined
      );

      if (selectedDate && (!res?.success || !Array.isArray(res.events) || res.events.length === 0)) {
        res = await (backendApi as any).getPMCalendarEvents?.(siteCode);
      }

      if (res?.success && Array.isArray(res.events)) {
        const today = new Date().toISOString().slice(0, 10);
        const mapped: PMTask[] = res.events.map((e: any) => {
          const task: PMTask = {
            id: String(e.id),
            title: e.title || e.name || 'Untitled Task',
            type: e.type || 'custom',
            date: e.date?.split('T')[0] || e.date || today,
            startTime: e.start_time || e.startTime || '09:00',
            endTime: e.end_time || e.endTime || '10:00',
            assignedTo: e.assigned_to || e.assignedTo || '',
            location: e.location || '',
            status: e.status || 'scheduled',
            priority: e.priority || 'medium',
            description: e.description || '',
            isRecurring: e.is_recurring ?? e.isRecurring ?? false,
            recurringPattern: e.recurring_pattern || e.recurringPattern || '',
            color: getTaskColor(e.type || 'custom'),
            equipmentName: e.equipment_name || e.equipmentName || ''
          };

          // Auto-mark overdue: past date + still "scheduled"
          if (task.date < today && task.status === 'scheduled' && !task.isRecurring) {
            return { ...task, status: 'overdue' };
          }
          return task;
        });

        mapped.sort((a, b) => {
          const bDate = new Date(`${b.date}T${b.startTime || '00:00'}`).getTime();
          const aDate = new Date(`${a.date}T${a.startTime || '00:00'}`).getTime();
          return bDate - aDate;
        });

        setTasks(mapped);
        saveToCache(mapped);
      } else {
        setTasks([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load PM tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad && siteCode) {
      fetchTasks();
    }
  }, [siteCode, selectedDate, autoLoad]);

  return {
    tasks,
    setTasks,
    loading,
    error,
    refetch: fetchTasks
  };
};

// Helper to get task color based on type
const getTaskColor = (taskType: string): string => {
  const colorMap: Record<string, string> = {
    'module-cleaning': '#0084CF',
    'grass-cutting': '#22c55e',
    'daily': '#f59e0b',
    'weekly': '#0066A3',
    'monthly': '#0084CF',
    'quarterly': '#f97316',
    'yearly': '#ef4444',
    'inverter': '#eab308',
    'tracker': '#10b981',
    'electrical': '#0084CF'
  };
  return colorMap[taskType] || '#64748b';
};
